import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { Card } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useGibRating } from "@/hooks/useGibRating";
import { useSprints } from "@/hooks/useSprints";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Info, Star } from "lucide-react";
import { Badge } from "@/components/ui/badge";

const KnowYourGib = () => {
  const navigate = useNavigate();
  const [userId, setUserId] = useState<string | null>(null);

  useEffect(() => {
    supabase.auth.getUser().then(({ data: { user } }) => {
      setUserId(user?.id || null);
    });
  }, []);

  const { gibRating, isLoading: isLoadingRating, calculateGibRating } = useGibRating(userId || undefined);
  const { sprints, isLoading: isLoadingSprints } = useSprints(userId || undefined);


  // Default metrics for users without data
  const defaultMetrics = [
    { name: "Quality of Contributions (Q)", score: 0, weight: 0.35, description: "Peer-reviewed score for accuracy, relevance, and utility of knowledge contributions" },
    { name: "Collaboration (C)", score: 0, weight: 0.20, description: "Number of peer-builds, diversity of wallets involved, and feedback from peers" },
    { name: "Engagement (E)", score: 0, weight: 0.15, description: "Frequency and consistency of participation in knowledge base activities" },
    { name: "Performance with GenAI (P)", score: 0, weight: 0.20, description: "Effectiveness in using AI to generate impactful prompts and solutions" },
    { name: "Adaptability (A)", score: 0, weight: 0.10, description: "Ability to adjust to new tasks, challenges, or team dynamics" },
  ];

  const individualMetrics = gibRating ? [
    { name: "Quality of Contributions (Q)", score: Number(gibRating.quality_score) / 10, weight: 0.35, description: "Peer-reviewed score for accuracy, relevance, and utility of knowledge contributions" },
    { name: "Collaboration (C)", score: Number(gibRating.collaboration_score) / 10, weight: 0.20, description: "Number of peer-builds, diversity of wallets involved, and feedback from peers" },
    { name: "Engagement (E)", score: Number(gibRating.engagement_score) / 10, weight: 0.15, description: "Frequency and consistency of participation in knowledge base activities" },
    { name: "Performance with GenAI (P)", score: Number(gibRating.genai_performance_score) / 10, weight: 0.20, description: "Effectiveness in using AI to generate impactful prompts and solutions" },
    { name: "Adaptability (A)", score: Number(gibRating.adaptability_score) / 10, weight: 0.10, description: "Ability to adjust to new tasks, challenges, or team dynamics" },
  ] : defaultMetrics;

  const overallRating = gibRating 
    ? (Number(gibRating.overall_rating) / 10).toFixed(1)
    : "0.0";

  return (
    <div className="min-h-screen bg-muted">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Card className="p-8">
          <div className="flex justify-between items-center mb-8">
            <div>
              <h1 className="font-serif text-3xl font-bold text-primary">Know Your Gib</h1>
            </div>
            {userId && (
              <Button 
                onClick={() => calculateGibRating(userId)}
                variant="outline"
              >
                Recalculate Rating
              </Button>
            )}
          </div>
          
          <Tabs defaultValue="rating" className="space-y-8">
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="rating">GibRating</TabsTrigger>
              <TabsTrigger value="sprints">Sprint History</TabsTrigger>
            </TabsList>

            <TabsContent value="rating" className="space-y-8">
              {/* Overall Score Display */}
              <div className="text-center py-8 border-b">
                <div className="inline-block">
                  {isLoadingRating ? (
                    <div className="text-6xl font-bold text-accent">...</div>
                  ) : (
                    <div className="flex items-baseline gap-2">
                      <span className="text-7xl font-bold text-accent">{overallRating}</span>
                      <span className="text-2xl text-muted-foreground">/10</span>
                    </div>
                  )}
                  <p className="text-sm text-muted-foreground mt-3">
                    {gibRating ? (
                      <>
                        Based on {gibRating.total_sprints_completed} sprint{gibRating.total_sprints_completed !== 1 ? 's' : ''} and {gibRating.total_contributions} contribution{gibRating.total_contributions !== 1 ? 's' : ''}
                      </>
                    ) : (
                      'Launch your first sprint to build your GibRating'
                    )}
                  </p>
                </div>
              </div>

              {/* Two-Category Breakdown */}
              <div className="space-y-6">
                <h3 className="font-semibold text-lg">Rating Components</h3>
                
                <div className="space-y-6">
                  {/* Personal Contribution */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <h4 className="font-medium text-foreground">Personal Contribution</h4>
                      <span className="text-xl font-mono text-accent">
                        {gibRating ? ((Number(gibRating.engagement_score) + Number(gibRating.genai_performance_score)) / 20).toFixed(1) : '0.0'}/10
                      </span>
                    </div>
                    <Progress 
                      value={gibRating ? ((Number(gibRating.engagement_score) + Number(gibRating.genai_performance_score)) / 2) : 0} 
                      className="h-3"
                    />
                    <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Engagement consistency
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        AI collaboration quality
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Knowledge contributions
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Peer connection breadth
                      </div>
                    </div>
                  </div>

                  {/* Sprint Collaboration */}
                  <div className="space-y-3">
                    <div className="flex justify-between items-baseline">
                      <h4 className="font-medium text-foreground">Sprint Collaboration</h4>
                      <span className="text-xl font-mono text-accent">
                        {gibRating ? ((Number(gibRating.quality_score) + Number(gibRating.collaboration_score) + Number(gibRating.adaptability_score)) / 30).toFixed(1) : '0.0'}/10
                      </span>
                    </div>
                    <Progress 
                      value={gibRating ? ((Number(gibRating.quality_score) + Number(gibRating.collaboration_score) + Number(gibRating.adaptability_score)) / 3) : 0} 
                      className="h-3"
                    />
                    <div className="grid grid-cols-2 gap-3 text-xs text-muted-foreground mt-2">
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Peer reactions to work
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Collaboration diversity
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Adaptability in teams
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-primary"></div>
                        Contribution timeliness
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* What is GibRating */}
              <div className="mt-8 p-6 bg-muted/30 border border-border rounded-lg">
                <div className="flex items-start gap-3">
                  <Info className="w-5 h-5 text-primary mt-0.5 flex-shrink-0" />
                  <div className="space-y-4">
                    <div>
                      <h3 className="font-semibold text-base mb-2">What is a GibRating?</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        Your collaborative fitness in AI-augmented knowledge work—measured through Personal Contribution 
                        (engagement and AI collaboration quality) and Sprint Collaboration (peer reactions to your work). 
                        The proprietary algorithm ensures it reflects genuine value, not gaming behavior.
                      </p>
                    </div>

                    <div className="pt-3 border-t border-border">
                      <p className="text-xs text-muted-foreground flex items-start gap-2">
                        <Star className="w-4 h-4 text-primary mt-0.5 flex-shrink-0" />
                        <span>
                          A high GibRating signals the collaborative mindset needed to thrive alongside AI, not compete against it.
                        </span>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Call to Action */}
              {(!gibRating || gibRating.total_sprints_completed === 0) && (
                <div className="text-center py-6">
                  <p className="text-muted-foreground mb-4">
                    Ready to start building your GibRating?
                  </p>
                  <Button onClick={() => navigate('/prompt-architecture-sprint')}>
                    Launch Your First Sprint
                  </Button>
                </div>
              )}
            </TabsContent>

            <TabsContent value="sprints" className="space-y-8">
              <div className="text-center mb-8">
                <h2 className="font-serif text-2xl font-semibold text-primary mb-2">Sprint History</h2>
                <p className="text-muted-foreground">
                  {isLoadingSprints ? "Loading..." : `${sprints?.length || 0} sprints completed`}
                </p>
              </div>

              {sprints && sprints.length > 0 ? (
                <Accordion type="single" collapsible className="w-full">
                  {sprints.map((sprint: any) => (
                    <AccordionItem key={sprint.id} value={sprint.id}>
                      <AccordionTrigger>
                        <div className="flex justify-between items-center w-full pr-4">
                          <span className="font-medium">{sprint.objective}</span>
                          <div className="flex items-center gap-4">
                            <span className={`px-2 py-1 rounded text-xs ${
                              sprint.status === 'completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                            }`}>
                              {sprint.status}
                            </span>
                            {sprint.sprint_gib_rating && (
                              <span className="text-sm font-mono">
                                Rating: {Number(sprint.sprint_gib_rating).toFixed(1)}
                              </span>
                            )}
                          </div>
                        </div>
                      </AccordionTrigger>
                      <AccordionContent>
                        <div className="space-y-4 pt-4">
                          <div>
                            <p className="text-sm text-muted-foreground">
                              Created: {new Date(sprint.created_at).toLocaleDateString()}
                            </p>
                            {sprint.completed_at && (
                              <p className="text-sm text-muted-foreground">
                                Completed: {new Date(sprint.completed_at).toLocaleDateString()}
                              </p>
                            )}
                          </div>
                          <div>
                            <h4 className="font-medium mb-2">Contributions</h4>
                            <div className="space-y-2">
                              {sprint.sprint_contributions?.map((contribution: any) => (
                                <div key={contribution.id} className="p-3 bg-muted rounded">
                                  <div className="flex justify-between items-start">
                                    <div>
                                      <span className="font-medium text-sm capitalize">{contribution.stage}</span>
                                      <p className="text-sm text-muted-foreground mt-1">
                                        {contribution.contribution_text}
                                      </p>
                                    </div>
                                    {contribution.contributor_rating && (
                                      <span className="text-sm font-mono">
                                        {contribution.contributor_rating}/5
                                      </span>
                                    )}
                                  </div>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </AccordionContent>
                    </AccordionItem>
                  ))}
                </Accordion>
              ) : (
                <div className="text-center py-12">
                  <p className="text-muted-foreground">
                    No sprints yet. Launch your first Prompt Architecture Sprint to start building your GibRating!
                  </p>
                  <Button 
                    className="mt-4"
                    onClick={() => navigate('/prompt-architecture-sprint')}
                  >
                    Launch a Sprint
                  </Button>
                </div>
              )}
            </TabsContent>
          </Tabs>

        </Card>
      </main>
    </div>
  );
};

export default KnowYourGib;